import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatINR, formatDistance, resolveImage, formatSize } from "../lib/api";
import { ArrowLeft, MapPin, Train, TrendingUp, Home, Layers, Building, Armchair, CheckCircle, Crown, BadgeCheck, Play, Lock, Navigation, Sparkles, Wallet, CalendarDays, Users, Timer, Briefcase, Percent, ShieldCheck, Hammer, Heart, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import WhatsAppShareButton from "./WhatsAppShareButton";
import AuthGate from "../components/AuthGate";
import usePageTitle from "../lib/usePageTitle";
import PropertyMiniMap from "../components/PropertyMiniMap";
import PageHeader from "../shared/components/PageHeader";
// v2.71.67 — Universal property surfaces (notes + role-aware action bar)
import PropertyNotesSection from "../shared/components/PropertyNotesSection";
import PropertyActionBar, { INTERNAL_ROLES } from "../shared/components/PropertyActionBar";
// v2.70 Phase 4 — Property-detail subcomponents
// v2.71.23 — Moved to features/property/components/
import ImageGallery from "../features/property/components/ImageGallery";
import SectionBoundary from "../shared/components/SectionBoundary";
import EMICalculator from "../features/property/components/EMICalculator";
import ROICalculator from "../features/property/components/ROICalculator";
import SimilarProperties from "../features/property/components/SimilarProperties";
import DealerInventoryReveal from "../features/property/components/DealerInventoryReveal";
import CompetingProjectsNearby from "../features/property/components/CompetingProjectsNearby";
import NearbyPlaces from "../features/property/components/NearbyPlaces";
import MobileBottomBar from "../features/property/components/MobileBottomBar";
import { PropertyDetailSkeleton } from "../components/Skeletons";
// v2.89 — Schema-driven detail sections (single source of truth with
// the Add/Edit form Review step).
import PropertyDetailSections from "../components/PropertyDetailSections";
// v2.78 Phase A — single source of truth for canonical display strings + tag dedup
import { normalizePropertyType, normalizeBhk, deriveSourceLabel, derivePublicSourceLabel, buildDisplayTags } from "../shared/utils/propertyNormalize";
// v2-listing-schema iter_111 — schema-driven More Details renderer with dedupe
import { getDetailFields } from "../lib/listingSchema";
// v2.79 Phase 1 — Cross-session real-time sync. When an admin edits / approves /
// rejects / sold-flags / deletes THIS property from any other tab or session,
// the detail page must re-hydrate instantly (no manual reload).
import { useDataSync } from "../lib/dataSync";
// v2.71.24 — Global state: record this view in the client-side trail.
import { useRecordView } from "../store";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [savingFav, setSavingFav] = useState(false);
  const [callbackBusy, setCallbackBusy] = useState(false);
  const [callbackSent, setCallbackSent] = useState(false);
  const { isAdmin, isTeamMember, user, tier, loading: authLoading } = useAuth();
  const { listingSchema } = useConfig();
  // v2.71.24 — Action selector (stable, no re-render subscription).
  const recordView = useRecordView();
  const fullView = isAdmin || isTeamMember;
  // MOVED TO TOP: const canRevealContact = useCanAccess(FEATURES.CONTACT_REVEAL);

  // iter_111 — Schema-driven More Details list with zone-based dedupe.
  // The schema's display_zones.moreDetails flag (set per-field by
  // the seed / admin via Listing Schema Builder) decides what shows up
  // here. `getDetailFields` automatically:
  //   • skips fields whose displayZones.internalOnly = true
  //   • skips fields where role_visibility[viewer] = false
  //   • dedupes by canonical key + aliases so the same value never
  //     renders twice even if multiple legacy aliases resolve to it
  //   • respects layout-owned renderers (media-gallery / link-google-maps /
  //     phone-tel / internal-id) by leaving them OUT of moreDetails
  //     (those flow through their dedicated layout slots instead)
  // v2.91 — customFieldRows kept for future admin/CRM consumers but no
  // longer rendered on the public detail page (schema-driven sections
  // replace the duplicate "More Details" surface). Mark the memo result
  // with an underscore-prefix to signal "computed but currently unused".
  const _customFieldRows = useMemo(() => {
    if (!p) return [];
    const toTitle = (s) =>
      String(s)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    return getDetailFields(listingSchema, p, "moreDetails", { role: "public" }).map((row) => {
      let display = row.value;
      const renderer = row.displayRenderer;
      if (renderer === "toggle") display = row.value ? "Yes" : "No";
      else if (Array.isArray(row.value)) display = row.value.join(", ");
      else if (row.field?.type === "dropdown" && typeof row.value === "string" && row.value) {
        display = toTitle(row.value);
      }
      return { key: row.key, label: row.label, value: String(display) };
    });
  }, [p, listingSchema]);
  void _customFieldRows;  // v2.91 — silence unused-var lint while we keep the memo for future consumers

  // Set per-property tab title for the human view
  usePageTitle(
    p
      ? `${p.bhk || ""} ${p.property_type || "Property"} in ${p.main_area || p.area || "Delhi NCR"} | HouseEntry`.trim()
      : null
  );

  useEffect(() => {
    // v2.71.3 — Public access: fetch the property for ALL viewers including
    // anonymous (shared link from WhatsApp etc.). Server still applies role-based
    // stripping (map coords, contact info) via strip_for_role(), so sensitive
    // data stays protected without blocking the page.
    api.get(`/properties/${id}`).then((r) => {
      setP(r.data);
      setNotes(r.data.notes || "");
      setSaved(!!r.data.is_saved);
      setSavedCount(r.data.saved_count || 0);
      // v2.71.24 — Record the view in the global trail (localStorage-persisted).
      recordView({
        id: r.data.id,
        title: r.data.title,
        area: r.data.main_area || r.data.area,
        price: r.data.price,
        image: (r.data.images && r.data.images[0]) || null,
      });
      // v2.71.67 — Behavioural signal for auto-leads pipeline (legacy comment
      // mentioned SmartEnquiryPopup which has been retired).
      try {
        window.dispatchEvent(new CustomEvent("he:property-view", {
          detail: { id: r.data.id, area: r.data.main_area || r.data.area, title: r.data.title }
        }));
      } catch { /* fire-and-forget */ }
    }).catch((err) => {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403) setError({ premium: true, msg });
      else setError({ premium: false, msg: msg || "Property not found" });
    });
    // recordView is a stable Zustand action — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // v2.79 Phase 1 — Re-hydrate on cross-session mutation of THIS property.
  // The data.sync WS broadcast carries { action, id, patch? }. We narrow to
  // events whose id matches the page we're on; for `delete` we toast and
  // navigate back; for any other action (patch / generic emit without id)
  // we silently refetch the canonical doc. No scroll reset, no loader.
  useDataSync(["properties"], (payload) => {
    if (!id) return;
    // Skip generic emits that target other properties.
    if (payload && payload.id && payload.id !== id) return;
    if (payload && payload.action === "delete" && payload.id === id) {
      toast("This listing was removed.");
      nav(-1);
      return;
    }
    // Silent re-fetch — preserves scroll, gallery position, notes editor state.
    api.get(`/properties/${id}`).then((r) => {
      setP(r.data);
      setSaved(!!r.data.is_saved);
      setSavedCount(r.data.saved_count || 0);
    }).catch(() => { /* swallow — keep stale rather than blank */ });
  });

  // v2.71.3 — Soft engagement banner: appears once for anon visitors after
  // 30 s of viewing, dismissable for the session. Non-blocking — does NOT
  // gate any content. Promotes save/sign-in as an opt-in benefit.
  // v2.71.5 — Wait until auth has finished loading before considering the
  // visitor "anonymous". Prevents the banner from queuing during the brief
  // `/auth/me` round-trip and then showing to an already-logged-in user.
  const [showEngageBanner, setShowEngageBanner] = useState(false);
  useEffect(() => {
    if (authLoading || user || !p) return;
    let dismissed = false;
    try { dismissed = !!sessionStorage.getItem("he-engage-banner-dismissed"); } catch { /* sessionStorage disabled */ }
    if (dismissed) return;
    const t = setTimeout(() => setShowEngageBanner(true), 30000);
    return () => clearTimeout(t);
  }, [user, p, authLoading]);

  // v2.71.82 — Soft sign-in prompt when a background API on this page
  // returns 401 (typical cause: session expired while user was reading).
  // The global axios interceptor now FIRES `he:auth-required` instead of
  // hard-redirecting on public pages — we react by opening the AuthGate
  // so the user can re-authenticate without losing their place.
  useEffect(() => {
    const onAuthRequired = (e) => {
      // Don't keep popping if it already happens to be open.
      setShowAuthGate((prev) => prev || true);
      // eslint-disable-next-line no-console
      console.info("[PropertyDetail] auth required — opening sign-in modal:", e?.detail);
    };
    window.addEventListener("he:auth-required", onAuthRequired);
    return () => window.removeEventListener("he:auth-required", onAuthRequired);
  }, []);

  if (error?.premium) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 bg-[#FAFAFA]">
        <div className="max-w-md text-center bg-white border border-[#E4E4E7] p-10 rounded-sm" data-testid="premium-locked">
          <Crown size={28} className="mx-auto text-[#0055FF] mb-3" />
          <h1 className="font-heading font-bold text-3xl tracking-tight">Premium-only listing</h1>
          <p className="text-[14px] text-[#52525B] mt-3 mb-6">
            This is a Priority listing reserved for Premium subscribers.
          </p>
          <Link to="/pricing"><Button className="btn-brand rounded-sm"><Crown size={14} className="mr-1.5" /> Upgrade to Premium</Button></Link>
        </div>
      </div>
    );
  }
  if (error) return <div className="max-w-[1200px] mx-auto p-10 text-[#A1A1AA]">{error.msg}</div>;
  if (!p) return <PropertyDetailSkeleton />;

  // v2.71.67 — Inline notes save. Sends only the notes patch; backend handles
  // permission check (admin-only). Exposed via PropertyNotesSection edit mode.
  const saveNotes = async (nextNotes) => {
    setSaving(true);
    try {
      await api.put(`/properties/${id}`, {
        title: p.title, price: p.price, area: p.area, bhk: p.bhk,
        property_type: p.property_type, size_sqft: p.size_sqft, floor: p.floor,
        furnishing: p.furnishing, status: p.status, source: p.source,
        dealer_name: p.dealer_name, contact: p.contact,
        google_maps_link: p.google_maps_link, distance_metro_m: p.distance_metro_m,
        latitude: p.latitude, longitude: p.longitude,
        images: p.images, monthly_rent: p.monthly_rent, notes: nextNotes,
        best_deal: p.best_deal, premium_deal: p.premium_deal,
      });
      setNotes(nextNotes);
      toast.success("Notes saved");
    } catch (e) {
      toast.error("Only admins can save notes");
    }
    setSaving(false);
  };

  const images = (p.images || []).length ? p.images : [null];
  // (mainImg derivation now lives inside ImageGallery)
  const videos = p.videos || [];

  // v2.70 Phase 4.1 — Intent-aware UI derivations (Sale / Rent / Lease)
  const intent = (p.listing_intent || "sale").toLowerCase();
  const isSale = intent === "sale";
  const isRent = intent === "rent";
  const isLease = intent === "lease";
  const sd = p.sale_details || {};
  const rd = p.rent_details || {};
  const ld = p.lease_details || {};

  const primaryPrice = isRent
    ? (rd.monthly_rent || p.monthly_rent || p.price || 0)
    : isLease
      ? (ld.monthly_rent || p.monthly_rent || p.price || 0)
      : (sd.sale_price || p.price || 0);
  const primaryPriceLabel = isRent ? "Monthly Rent" : isLease ? "Monthly Lease" : "Asking Price";
  const primaryPriceSuffix = (isRent || isLease) ? "/mo" : "";

  // Secondary card (replaces ROI for rent/lease)
  const securityDep = (isRent ? rd.security_deposit : isLease ? ld.security_deposit : null) || 0;
  const secondaryCard = isSale
    ? { label: "ROI (Yearly)", value: `${(p.roi || 0).toFixed(2)}%`, testid: "detail-roi" }
    : isRent
      ? { label: "Security Deposit", value: securityDep ? formatINR(securityDep) : "—", testid: "detail-security-deposit" }
      : { label: "Lease Duration", value: ld.lease_duration_months ? `${ld.lease_duration_months} months` : "—", testid: "detail-lease-duration" };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };
  const titleCase = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

  const onToggleSave = async () => {
    if (savingFav) return;
    // v2.71.3 — Soft-prompt sign-in instead of silently failing for anon users
    if (!user) { setShowAuthGate(true); return; }
    setSavingFav(true);
    const optimistic = !saved;
    setSaved(optimistic);
    setSavedCount((c) => Math.max(0, c + (optimistic ? 1 : -1)));
    try {
      const r = await api.post(`/properties/${id}/save`);
      setSaved(!!r.data.saved);
      setSavedCount(r.data.saved_count || 0);
      toast.success(r.data.saved ? "Saved" : "Removed from saved");
    } catch {
      setSaved(!optimistic);
      setSavedCount((c) => Math.max(0, c + (optimistic ? -1 : 1)));
      toast.error("Couldn't update saved");
    }
    setSavingFav(false);
  };

  // v2.71.67 — Request Callback. Enquiry-system removed per refactor spec —
  // we go straight to lead creation. If the user is anonymous OR has no
  // phone on file, we prompt the AuthGate (sign-in) instead of an enquiry
  // form. Auto-leads pipeline (POST /enquiries with kind=callback_request)
  // is preserved; CRM continues to receive these as callback leads.
  const onRequestCallback = async () => {
    if (!user) { setShowAuthGate(true); return; }
    const phone = (user.phone || "").trim();
    if (!phone) {
      toast.error("Please add a phone number in your profile to receive callbacks.");
      return;
    }
    if (callbackBusy) return;
    setCallbackBusy(true);
    try {
      await api.post("/enquiries", {
        property_id: id,
        name: user.name || user.email,
        phone,
        message: "Callback requested via property detail page",
        kind: "callback_request",
      });
      setCallbackSent(true);
      toast.success("Callback requested — our team will call you shortly.");
    } catch (err) {
      toast.error("Couldn't request callback. Please try again.");
    }
    setCallbackBusy(false);
  };

  // v2.35 — Freemium lock was used on the map external-link button.
  // v2.77 — That inline button is removed; PropertyMiniMap handles its own
  // gating via property.map_locked. The flag below is intentionally retained
  // for any future premium CTAs but is no longer surfaced in this render.

  return (
    <div className="min-h-screen bg-white">
      {/* v2.71.51 — Integrated header replaces the floating Back pill. */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <PageHeader
            title={p.title}
            subtitle={p.main_area || p.area || null}
            backLabel="Listings"
            home="/listings"
          />
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8" data-testid="property-detail-container">

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 sm:gap-8 lg:gap-10">
          <div data-testid="property-gallery-wrap" className="min-w-0 overflow-hidden">
            {p.status === "Sold" && (
              <div
                data-testid="sold-banner"
                className="mb-3 p-4 bg-[#09090B] text-white border border-[#09090B] rounded-sm flex items-center justify-between"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/60">Closed Deal</div>
                  <div className="font-heading font-bold text-lg tracking-tight">This property has been sold</div>
                  <p className="text-[12px] text-white/70 mt-0.5">Enquiries are closed. Explore other available listings.</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm bg-white/10 border border-white/20">Sold</span>
              </div>
            )}
            {/* v2.70 Phase 4 — Swipeable carousel + lightbox */}
            <SectionBoundary name="gallery">
              <ImageGallery images={p.images || []} title={p.title} sold={p.status === "Sold"} />
            </SectionBoundary>

            {/* Videos (v2.34 — PART 1) */}
            {videos.length > 0 && (
              <SectionBoundary name="videos">
                <div className="mt-6" data-testid="property-videos">
                  <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-3 flex items-center gap-2">
                    <Play size={12} /> Walkthrough Videos
                    <span className="text-[10px] font-normal text-[#A1A1AA] normal-case tracking-normal">({videos.length})</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {videos.map((src, i) => (
                      <video
                        key={`${src}-${i}`}
                        src={resolveImage(src)}
                        controls
                        preload="metadata"
                        playsInline
                        className="w-full aspect-video bg-black border border-[#E4E4E7] rounded-sm"
                        data-testid={`property-video-${i}`}
                      />
                    ))}
                  </div>
                </div>
              </SectionBoundary>
            )}

            {/* Premium Leaflet mini-map (v2.39 — replaces iframe)
                v2.85 Phase A — Anonymous viewers get an approximate
                circle (300m radius) instead of the exact pin. Signed-in
                users continue to see the exact location per existing
                premium/role gating. Privacy by default. */}
            <SectionBoundary name="mini-map">
              <div className="mt-6" data-testid="property-map-section">
                <PropertyMiniMap property={p} approximate={!user} />
              </div>
            </SectionBoundary>

            {/* v2.70 Phase 4 — Nearby places via OpenStreetMap Overpass */}
            {p.latitude && p.longitude && !p.map_locked && (
              <SectionBoundary name="nearby-places">
                <div className="mt-6">
                  <NearbyPlaces latitude={p.latitude} longitude={p.longitude} />
                </div>
              </SectionBoundary>
            )}

            {/* v2.71.63 — Marketplace inventory lock banner for dealers/builders
                viewing other people's listings. Backend strips lat/lng/address
                on these; this banner makes the lock state explicit. */}
            {p.inventory_locked && (
              <div
                data-testid="dealer-marketplace-lock"
                className="mt-6 relative overflow-hidden rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-[#0055FF] to-[#7C3AED] text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                    <Lock size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-amber-200 text-[9.5px] uppercase tracking-[0.16em] font-bold text-[#92400E] mb-1.5">
                      <Crown size={9} /> Marketplace inventory
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 leading-snug">Exact location, owner contact &amp; market intel hidden</div>
                    <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
                      You're viewing a listing owned by another partner. Premium dealer plans unlock exact coordinates, owner contact, and market intelligence — or route inquiries via HouseEntry admin below.
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to="/pricing"
                        className="inline-flex items-center gap-1.5 btn-gradient rounded-xl px-4 py-2 text-[12.5px] font-bold shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-95 transition-all"
                        data-testid="marketplace-lock-upgrade-btn"
                      >
                        <Sparkles size={12} /> Upgrade to unlock
                      </Link>
                      <Link
                        to="/plans-preview"
                        className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 bg-white rounded-xl px-3.5 py-2 text-[12px] font-semibold hover:border-[#0055FF] hover:text-[#0055FF] active:scale-95 transition-all"
                        data-testid="marketplace-lock-compare-btn"
                      >
                        Compare plans
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF] mb-2 flex items-center gap-2">
                {/* v2.78 Phase A — canonical property_type display */}
                <MapPin size={12} /> {p.area} · {normalizePropertyType(p.property_type) || "Property"}
              </div>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090B] leading-tight">{p.title}</h1>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {/* v2.70 Phase 4.1 — Intent badge (Sale/Rent/Lease) */}
                <span
                  className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${
                    isRent ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : isLease ? "bg-[#EDE9FE] text-[#5B21B6] border-[#DDD6FE]" : "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]"
                  }`}
                  data-testid={`detail-intent-badge-${intent}`}
                >
                  For {isRent ? "Rent" : isLease ? "Lease" : "Sale"}
                </span>
                {/* v2.85 Phase A — Public "Posted By" badge. Shows
                    Owner / Dealer / Builder / Listed By so customers know
                    the source of the listing at a glance. Admin role
                    collapses to "Listed By" for public viewers. */}
                {(() => {
                  const postedBy = derivePublicSourceLabel(p);
                  const colorMap = {
                    Owner:   "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
                    Dealer:  "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
                    Builder: "bg-[#FDF4FF] text-[#86198F] border-[#F5D0FE]",
                  };
                  const cls = colorMap[postedBy] || "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]";
                  return (
                    <span
                      className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${cls}`}
                      data-testid="detail-posted-by-badge"
                      title={`This property was listed by a ${postedBy.toLowerCase() === "listed by" ? "verified partner" : postedBy.toLowerCase()}`}
                    >
                      {postedBy.toLowerCase() === "listed by" ? "Listed By" : `Posted by ${postedBy}`}
                    </span>
                  );
                })()}
                {/* v2.78 Phase A — tag dedup. buildDisplayTags() returns the
                    boolean-derived curation chips (Best Deal / Premium Deal /
                    Featured) PLUS the editorial tags array, with duplicates
                    removed so a single label never renders twice. */}
                {buildDisplayTags(p).map((t) => (
                  <span
                    key={t}
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${
                      t === "Near Metro" ? "tag-metro" : t === "Best Deal" ? "tag-best" : t === "Featured" ? "bg-[#0055FF] text-white border border-[#0055FF]" : t === "Priority" ? "bg-[#0055FF] text-white border border-[#0055FF]" : "tag-roi"
                    }`}
                    data-testid={`detail-tag-${t.replace(/ /g, "-")}`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#0055FF] text-white p-4 rounded-sm">
                <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">{primaryPriceLabel}</div>
                <div className="font-heading font-bold text-2xl font-mono-data mt-1" data-testid="detail-price">
                  {formatINR(primaryPrice)}{primaryPriceSuffix && <span className="text-[12px] font-normal text-white/70 ml-1">{primaryPriceSuffix}</span>}
                </div>
              </div>
              <div className="bg-[#09090B] text-white p-4 rounded-sm">
                <div className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">{secondaryCard.label}</div>
                <div className="font-heading font-bold text-2xl font-mono-data mt-1" data-testid={secondaryCard.testid}>{secondaryCard.value}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-y border-[#E4E4E7] py-5">
              {/* v2.78 Phase A — canonical bhk + property_type display.
                  v2.91 — "Size" renamed to "Built-up Area" to match the
                  form and the schema. Legacy `p.floor` free-text is only
                  used when the newer `custom_fields.current_floor` value
                  is empty, keeping older records visible without an
                  extra migration. */}
              <Spec icon={<Home size={13} />} label="BHK" value={normalizeBhk(p.bhk) || "—"} />
              <Spec icon={<Layers size={13} />} label="Built-up Area" value={formatSize(p.size_sqft, p.size_unit)} />
              <Spec
                icon={<Building size={13} />}
                label="Floor"
                value={
                  (p.custom_fields && p.custom_fields.current_floor) ||
                  p.floor ||
                  "—"
                }
              />
              <Spec icon={<Armchair size={13} />} label="Furnishing" value={(isRent && rd.furnishing) || p.furnishing || "—"} />
              <Spec icon={<CheckCircle size={13} />} label="Status" value={p.status} />
              <Spec icon={<Train size={13} />} label="Metro" value={formatDistance(p.distance_metro_m)} highlight={p.distance_metro_m < 1000} />

              {/* v2.70 Phase 4.1 — Intent-aware specs */}
              {isSale && (
                <>
                  {sd.booking_amount > 0 && (
                    <Spec icon={<Wallet size={13} />} label="Booking Amount" value={formatINR(sd.booking_amount)} testid="spec-booking-amount" />
                  )}
                  {sd.possession_status && (
                    <Spec icon={<CheckCircle size={13} />} label="Possession" value={titleCase(sd.possession_status)} testid="spec-possession-status" />
                  )}
                  {sd.negotiable && (
                    <Spec icon={<TrendingUp size={13} />} label="Price" value="Negotiable" testid="spec-negotiable" />
                  )}
                  {sd.loan_available && (
                    <Spec icon={<BadgeCheck size={13} />} label="Loan" value="Available" testid="spec-loan-available" />
                  )}
                  {/* Expected income for investors */}
                  {(p.monthly_rent > 0) && (
                    <Spec icon={<TrendingUp size={13} />} label="Expected Monthly Rent" value={formatINR(p.monthly_rent)} />
                  )}
                  {(p.annual_rent > 0) && (
                    <Spec icon={<TrendingUp size={13} />} label="Annual Rent" value={formatINR(p.annual_rent)} />
                  )}
                </>
              )}

              {isRent && (
                <>
                  {(rd.maintenance_charges > 0) && (
                    <Spec icon={<Wallet size={13} />} label="Maintenance" value={`${formatINR(rd.maintenance_charges)}/mo`} testid="spec-maintenance" />
                  )}
                  {rd.available_from && (
                    <Spec icon={<CalendarDays size={13} />} label="Available From" value={formatDate(rd.available_from)} testid="spec-available-from" />
                  )}
                  {rd.preferred_tenant && (
                    <Spec icon={<Users size={13} />} label="Preferred Tenant" value={titleCase(rd.preferred_tenant)} testid="spec-preferred-tenant" />
                  )}
                  {rd.lock_in_period_months > 0 && (
                    <Spec icon={<Timer size={13} />} label="Lock-in" value={`${rd.lock_in_period_months} months`} testid="spec-lock-in-rent" />
                  )}
                </>
              )}

              {isLease && (
                <>
                  {ld.commercial_usage && (
                    <Spec icon={<Briefcase size={13} />} label="Usage" value={titleCase(ld.commercial_usage)} testid="spec-commercial-usage" />
                  )}
                  {ld.lock_in_period_months > 0 && (
                    <Spec icon={<Timer size={13} />} label="Lock-in" value={`${ld.lock_in_period_months} months`} testid="spec-lock-in-lease" />
                  )}
                  {ld.escalation_percent > 0 && (
                    <Spec icon={<Percent size={13} />} label="Rent Escalation" value={`${ld.escalation_percent}% every ${ld.escalation_cycle_years || 3} yr`} testid="spec-escalation" />
                  )}
                  {ld.fit_out_period_days > 0 && (
                    <Spec icon={<Hammer size={13} />} label="Fit-out Period" value={`${ld.fit_out_period_days} days`} testid="spec-fit-out" />
                  )}
                  {(ld.security_deposit > 0) && (
                    <Spec icon={<ShieldCheck size={13} />} label="Security Deposit" value={formatINR(ld.security_deposit)} testid="spec-lease-security" />
                  )}
                </>
              )}
            </div>

            {/* v2.89 — Schema-driven property detail sections (single
                source of truth with Add/Edit form Review step). Rendered
                right after the existing hero Spec grid + intent-specific
                Spec rows so the more-detailed section grid appears below
                the executive summary. Sections that are already covered
                by the legacy Spec rows above (Basic snapshot, Price,
                Contact) are hidden here to prevent duplication. */}
            <PropertyDetailSections
              property={p}
              fullView={fullView}
              hideSections={new Set(["basic", "price_charges", "contact", "location", "description", "documents"])}
            />

            {/* v2.91 — "More Details" block REMOVED. Every field that
                used to live here is now surfaced by the schema-driven
                PropertyDetailSections above (Rooms & Area, Floor &
                Building, Features & Amenities, Property Information).
                The `customFieldRows` variable + `getDetailFields` helper
                are still computed for other consumers (admin CRM, PDF
                export) but no longer rendered on the public detail page.
                Doing this eliminates the duplicate rows the audit flagged
                (BHK/Furnishing/Total Floors/Facing/Lift/Road Width etc.). */}

            {fullView && (p.dealer_name || p.contact || p.dealer_phone || p.resolved_phone || p.contact_state) && (() => {
              // v2.76 — Admin-only Contact Information block.
              // v2.78 Phase A — role label now comes from the shared
              // `deriveSourceLabel(submitted_by_role, source)` helper so
              // PropertyDetail / PropertyCard / Admin grid all render the
              // same value. Phone-priority + email-filter logic unchanged.
              //
              // v2.90 — Legacy contact resolver (see backend/contact_resolver.py).
              // If the doc itself has no dealer_phone / phone-shaped contact,
              // the backend fills `resolved_phone` from users.phone via
              // created_by → submitted_by_id → assigned_to_user_id. We also
              // render `contact_state` as a diagnostic sub-line so admins
              // instantly understand WHY a phone is missing on legacy
              // listings. Fields are present ONLY for admin/team.
              const roleLabel = deriveSourceLabel(p.submitted_by_role, p.source);
              const dealerPhone = (p.dealer_phone || "").trim();
              const legacyContact = (p.contact || "").trim();
              const resolvedPhone = (p.resolved_phone || "").trim();
              const looksLikeEmail = legacyContact.includes("@");
              const phoneToShow =
                dealerPhone ||
                (looksLikeEmail ? "" : legacyContact) ||
                resolvedPhone;
              const phoneDisplay = phoneToShow || "No Contact Number Available";
              // Diagnostic banner for admin/team viewers.
              const state = p.contact_state || "";
              const source = p.contact_source || "";
              const sourceLabel = {
                dealer_phone: "from dealer_phone field",
                contact: "from contact field",
                created_by_user: "from created_by user",
                submitted_by_user: "from submitted_by user",
                assigned_user: "from assigned user",
              }[source];
              const stateLabel = {
                available: sourceLabel ? `Resolved · ${sourceLabel}` : "",
                missing_relation: "No owner/dealer relation on record",
                missing_number: "Owner user has no phone on file",
                legacy_unmigrated: "Legacy bulk-import — no creator link",
                hidden_by_permission: "",
              }[state];
              const displayName = p.dealer_name || (p.resolved_name || "").trim() || "—";
              return (
                <div className={`p-4 rounded-sm border ${isAdmin ? "bg-[#FEF2F2] border-[#FECACA]" : "bg-[#FEF3C7] border-[#FDE68A]"}`} data-testid="admin-dealer-block">
                  <div className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${isAdmin ? "text-[#B91C1C]" : "text-[#92400E]"}`} data-testid="admin-contact-label">
                    {isAdmin ? `⛔ Admin Only — ${roleLabel} Contact` : `👥 Team Console — ${roleLabel} Contact`}
                  </div>
                  <div className="text-sm text-[#09090B]">
                    <div data-testid="admin-contact-name"><span className="text-[#52525B]">{roleLabel}:</span> <b>{displayName}</b></div>
                    <div data-testid="admin-contact-phone"><span className="text-[#52525B]">Mobile:</span> <b className={phoneToShow ? "" : "text-[#B91C1C]"}>{phoneDisplay}</b></div>
                    {p.source && <div data-testid="admin-contact-source"><span className="text-[#52525B]">Source:</span> <b>{p.source}</b></div>}
                    {stateLabel && (
                      <div className="text-[11px] text-[#71717A] mt-1" data-testid="admin-contact-state">
                        <span className="uppercase tracking-wider font-semibold">Contact Status:</span> {stateLabel}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {!fullView && (
              <div
                className="p-4 bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm flex items-start gap-3"
                data-testid="verified-by-house-entry"
              >
                <BadgeCheck size={20} className="text-[#15803D] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[#15803D]">Verified by House Entry</div>
                  <div className="text-[13px] text-[#166534] mt-0.5">
                    Verified by HouseEntry. Listing checked &amp; curated by our team.
                  </div>
                </div>
              </div>
            )}

            {/* v2.71.64 — Platform contact replaces owner contact for dealers/builders
                viewing marketplace listings. Inquiries route via admin instead of
                exposing owner phone/email. */}
            {p.inventory_locked && (
              <div
                className="p-4 rounded-2xl border border-[#BFDBFE] bg-gradient-to-br from-[#EFF6FF] via-white to-[#EFF6FF]/40"
                data-testid="marketplace-platform-contact"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-[#0055FF] text-white flex items-center justify-center shadow-sm">
                    <ShieldCheck size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#1E40AF] mb-1">
                      {p.platform_contact_label || "Route inquiries via HouseEntry admin"}
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 leading-snug">
                      {p.platform_contact_name || "HouseEntry Solutions"}
                    </div>
                    <a
                      href={`mailto:${p.platform_contact_email || "houseentrysolutions@gmail.com"}`}
                      className="mt-0.5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0055FF] hover:underline"
                      data-testid="marketplace-platform-email"
                    >
                      {p.platform_contact_email || "houseentrysolutions@gmail.com"}
                    </a>
                    <div className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">
                      Owner contact is hidden for marketplace inventory. Send your client lead to HouseEntry — we'll connect both parties and split the commission per dealer plan terms.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* v2.71.67 — Universal action bar replaces the inline button
                soup. Role-aware: customer roles (client/dealer/builder/guest)
                see WhatsApp · Call · Callback · Save in spec order; internal
                roles (admin/team) see no customer CTAs (they manage via
                their consoles). Enquiry button removed entirely.
                v2.77 — Removed duplicate "Open in Google Maps" and
                "View on Map" buttons. PropertyMiniMap's header already
                exposes both (Directions + Google Maps + Fullscreen toggle),
                so the inline duplicates were dead weight. Single source of
                truth for the property location is now <PropertyMiniMap />. */}
            <div className="flex flex-wrap gap-2">
              <WhatsAppShareButton property={p} testid="detail-whatsapp-share" />
            </div>

            <PropertyActionBar
              role={user?.role || null}
              user={user}
              property={p}
              saved={saved}
              savedCount={savedCount}
              savingFav={savingFav}
              callbackBusy={callbackBusy}
              callbackSent={callbackSent}
              onCallback={onRequestCallback}
              onToggleSave={onToggleSave}
              onLoginRequired={() => setShowAuthGate(true)}
            />

            {/* v2.71.67 — Universal notes section. No fixed height. No
                internal scroll. Natural-flow text with whitespace-pre-wrap.
                Admins can edit inline via the Edit affordance. */}
            <SectionBoundary name="notes">
              <PropertyNotesSection
                notes={notes}
                editable={isAdmin}
                saving={saving}
                onSave={saveNotes}
              />
            </SectionBoundary>
          </div>
        </div>

        {/* v2.70 Phase 4 — Calculators + Similar Properties section */}
        <div className="mt-6 sm:mt-10 space-y-4 sm:space-y-6 pb-24 lg:pb-6">
          {/* v2.70 Phase 4.1 — EMI + ROI are SALE-only (investment context). Rent/Lease pages skip them. */}
          {isSale && (
            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
              <SectionBoundary name="emi-calc">
                <EMICalculator defaultPrice={p.price || 0} />
              </SectionBoundary>
              <SectionBoundary name="roi-calc">
                <ROICalculator
                  defaultPrice={p.price || 0}
                  defaultRent={p.monthly_rent || Math.round((p.price || 0) * 0.005)}
                />
              </SectionBoundary>
            </div>
          )}
          {/* v2.71.63 — Dealers see a collapsed "Related Inventory" panel.
              v2.71.64 — Hidden on marketplace items (inventory_locked=true).
              v2.71.65 — Builders see "Competing Projects Nearby" (max 5,
              collapsed) per spec STEP 9 — a project-focused competitive
              awareness panel, NOT a similar-properties feed. */}
          <SectionBoundary name="related">
            {user?.role === "builder" ? (
              !p.inventory_locked ? (
                <CompetingProjectsNearby property={p} />
              ) : null
            ) : user?.role === "dealer" ? (
              !p.inventory_locked ? (
                <DealerInventoryReveal propertyId={p.id} />
              ) : null
            ) : (
              <SimilarProperties propertyId={p.id} />
            )}
          </SectionBoundary>
        </div>
      </div>
      {/* v2.71.3 — AuthGate available for soft-prompting on protected actions
          (Save, deep-engage banner CTA). Page itself remains publicly viewable.
          v2.71.67 — Also used as the sign-in prompt when an anon user clicks
          Request Callback (enquiry dialog removed). */}
      <AuthGate
        open={showAuthGate}
        onClose={() => setShowAuthGate(false)}
        action="save"
        returnTo={`/property/${id}`}
      />
      {/* v2.71.3 — Soft engagement banner (anon only, after 30 s, dismissable for session) */}
      {showEngageBanner && !user && (
        <div
          className="fixed bottom-20 lg:bottom-6 right-3 left-3 sm:left-auto sm:right-6 sm:max-w-sm z-40 bg-white border border-[#E4E4E7] rounded-2xl shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-4 duration-300"
          data-testid="anon-engage-banner"
        >
          <button
            type="button"
            onClick={() => {
              setShowEngageBanner(false);
              try { sessionStorage.setItem("he-engage-banner-dismissed", "1"); } catch { /* ignore */ }
            }}
            className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center rounded-full text-[#A1A1AA] hover:text-[#09090B] hover:bg-[#F4F4F5]"
            aria-label="Dismiss"
            data-testid="anon-engage-banner-dismiss"
          >
            <X size={14} />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <div className="h-10 w-10 rounded-full bg-[#EFF6FF] text-[#0055FF] flex items-center justify-center shrink-0">
              <Heart size={18} fill="currentColor" />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-[14px] text-[#09090B] leading-tight">Save this property?</div>
              <p className="text-[12px] text-[#52525B] mt-0.5 leading-snug">
                Get price-drop alerts, see similar listings, and chat with verified dealers.
              </p>
              <button
                type="button"
                onClick={() => setShowAuthGate(true)}
                className="mt-2.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0055FF] hover:text-[#0044CC]"
                data-testid="anon-engage-banner-cta"
              >
                Sign in free <ArrowLeft size={12} className="rotate-180" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* v2.70 Phase 4 — Sticky mobile bottom bar (hidden on lg+).
          v2.71.67 — Enquiry slot removed; bar mirrors PropertyActionBar.
          v2.87.x — Contact routing now delegated to lib/contactRouting.js
          via the bar's own internal helper (no direct dealer phone). */}
      <MobileBottomBar
        user={user}
        property={p}
        onCallback={onRequestCallback}
        onLoginRequired={() => setShowAuthGate(true)}
        disabled={INTERNAL_ROLES.has(user?.role) || p.status === "Sold"}
      />
    </div>
  );
}

const Spec = ({ icon, label, value, highlight, testid }) => (
  <div {...(testid ? { "data-testid": testid } : {})}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A1A1AA] flex items-center gap-1 mb-0.5">{icon} {label}</div>
    <div className={`text-sm font-semibold font-mono-data ${highlight ? "text-[#0055FF]" : "text-[#09090B]"}`}>{value}</div>
  </div>
);
